import { isDeclinedEvent } from "./eventResponse";

export const MEETING_REMINDER_THRESHOLDS = {
  SOON_MINUTES: 10,
  URGENT_MINUTES: 2,
};

export const MEETING_REMINDER_LEVELS = {
  NONE: "none",
  SOON: "soon",
  URGENT: "urgent",
};

export const MEETING_REMINDER_SESSION_KEY = "gaintime.shownMeetingReminders";

const MINUTE_MS = 60 * 1000;
const MAX_REMINDER_TITLE_LENGTH = 44;

export const formatMeetingReminderTitle = (title) => {
  const fallbackTitle = "Untitled meeting";
  const trimmedTitle =
    typeof title === "string" && title.trim() ? title.trim() : fallbackTitle;
  const capitalizedTitle =
    trimmedTitle.charAt(0).toUpperCase() + trimmedTitle.slice(1);

  if (capitalizedTitle.length <= MAX_REMINDER_TITLE_LENGTH) {
    return capitalizedTitle;
  }

  return `${capitalizedTitle.slice(0, MAX_REMINDER_TITLE_LENGTH - 3).trim()}...`;
};

export const getEventTimestamp = (value) => {
  if (!value) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

export const getMinutesUntilEvent = (startDateTime, now = Date.now()) => {
  const start = getEventTimestamp(startDateTime);
  if (!start) return null;

  return (start - now) / MINUTE_MS;
};

export const getMeetingReminderLevel = (startDateTime, now = Date.now()) => {
  const minutesUntilStart = getMinutesUntilEvent(startDateTime, now);

  if (minutesUntilStart === null || minutesUntilStart <= 0) {
    return MEETING_REMINDER_LEVELS.NONE;
  }

  if (minutesUntilStart <= MEETING_REMINDER_THRESHOLDS.URGENT_MINUTES) {
    return MEETING_REMINDER_LEVELS.URGENT;
  }

  if (minutesUntilStart <= MEETING_REMINDER_THRESHOLDS.SOON_MINUTES) {
    return MEETING_REMINDER_LEVELS.SOON;
  }

  return MEETING_REMINDER_LEVELS.NONE;
};

export const getCurrentReminderThreshold = (
  startDateTime,
  now = Date.now()
) => {
  const level = getMeetingReminderLevel(startDateTime, now);

  if (level === MEETING_REMINDER_LEVELS.URGENT) {
    return MEETING_REMINDER_THRESHOLDS.URGENT_MINUTES;
  }

  if (level === MEETING_REMINDER_LEVELS.SOON) {
    return MEETING_REMINDER_THRESHOLDS.SOON_MINUTES;
  }

  return null;
};

export const getNextReminderTransitionDelay = (
  startDateTime,
  now = Date.now()
) => {
  const start = getEventTimestamp(startDateTime);
  if (!start || start <= now) return null;

  const transitionTimes = [
    start - MEETING_REMINDER_THRESHOLDS.SOON_MINUTES * MINUTE_MS,
    start - MEETING_REMINDER_THRESHOLDS.URGENT_MINUTES * MINUTE_MS,
    start,
  ].filter((timestamp) => timestamp > now);

  if (transitionTimes.length === 0) return null;

  return Math.max(0, Math.min(...transitionTimes) - now + 250);
};

export const shouldSkipMeetingReminder = (event) => {
  if (!event) return true;
  if (isDeclinedEvent(event)) return true;
  if (event.hasEventEnded) return true;
  if (!getEventTimestamp(event.startDateTime || event.start)) return true;

  return false;
};

export const isReminderForActiveEmbeddedMeeting = (
  event,
  activeEmbeddedMeeting
) => {
  const eventId = event?.eventId || event?.id;

  return !!eventId && eventId === activeEmbeddedMeeting?.eventId;
};

export const buildMeetingReminderKey = (event, thresholdMinutes) => {
  const eventId = event?.eventId || event?.id;
  const startDateTime = event?.startDateTime || event?.start;

  if (!eventId || !startDateTime || !thresholdMinutes) return null;

  return `${eventId}:${startDateTime}:${thresholdMinutes}`;
};

export const getReminderMeetingSelection = (event) => {
  const eventId = event?.eventId || event?.id;
  const startTimestamp = getEventTimestamp(event?.startDateTime || event?.start);

  if (!eventId || !startTimestamp) return null;

  return {
    eventId,
    selectedDate: new Date(startTimestamp),
  };
};

export const getShownMeetingReminderKeys = (storage = window.sessionStorage) => {
  try {
    const rawValue = storage.getItem(MEETING_REMINDER_SESSION_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : [];

    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (_error) {
    return new Set();
  }
};

export const saveShownMeetingReminderKeys = (
  keys,
  storage = window.sessionStorage
) => {
  try {
    storage.setItem(MEETING_REMINDER_SESSION_KEY, JSON.stringify([...keys]));
  } catch (_error) {
    // Session storage can be unavailable in private or restricted contexts.
  }
};

export const createMeetingReminderMessage = ({
  event,
  thresholdMinutes,
  activeEmbeddedMeeting,
}) => {
  const title = formatMeetingReminderTitle(event?.title);
  const startsIn = `${thresholdMinutes} minute${
    thresholdMinutes === 1 ? "" : "s"
  }`;
  const isAnotherEmbeddedMeeting =
    activeEmbeddedMeeting?.eventId &&
    activeEmbeddedMeeting.eventId !== event?.eventId;

  if (isAnotherEmbeddedMeeting) {
    return `Another meeting starts in ${startsIn}: "${title}".`;
  }

  if (thresholdMinutes === MEETING_REMINDER_THRESHOLDS.SOON_MINUTES) {
    return `Heads up: "${title}" starts in ${startsIn}.`;
  }

  return `"${title}" starts in ${startsIn}.`;
};
